import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { Store } from "@ngrx/store";
import { UserAdminActions } from "@store/actions/user-admin/user-admin.actions";
import {
  selectUsers,
  selectUserInfo,
  selectUserRoles,
  selectUserAdminFilteredOrgs,
  selectUserAdminForm
} from "@store/selectors/user-admin/user-admin.selector";
import { MatIconModule } from "@angular/material/icon";
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
} from '@angular/forms';
import { CommonModule, formatDate } from "@angular/common";
import { AgGridAngular } from "ag-grid-angular";
import type { ColDef, GridOptions } from "ag-grid-community";
import {
  AllCommunityModule,
  ModuleRegistry,
} from "ag-grid-community";
import { MatDividerModule } from "@angular/material/divider";
import { Subscription } from "rxjs";
import { EditAddUserModalComponent } from "@components/modals/edit-add-user-modal/edit-add-user-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { User } from "@models/user.model";
import { IDropdown } from "@models/dropdown.model";
import { Organization } from "@models/organization.model";
import { EditDeleteRendererComponent } from "@components/shared/edit-delete-renderer/edit-delete-renderer.component";
import { myCustomTheme } from "../../constants/grid-themes";
import { distinctUntilChanged, takeUntil, debounceTime, filter,tap, delay } from 'rxjs/operators';
import { Subject, pipe } from 'rxjs';
import { selectIsSuperUserNotMatrixPPAAdmin, selectIsAdminNotSuperUser } from '@store/selectors/user/user.selectors';
import { RoleConstants, ROLE_GROUPS } from '../../constants/roles';


ModuleRegistry.registerModules([AllCommunityModule]);
@Component({
  selector: "app-user-admin",
  templateUrl: "./user-admin.component.html",
  styleUrl: "./user-admin.component.scss",
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    AgGridAngular,
    MatDividerModule,
  ],
})
export class UserAdminComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  private readonly store = inject(Store);
  readonly dialog = inject(MatDialog);
  readonly cdr = inject(ChangeDetectorRef);
  private ngUnsubscribe = new Subject<void>();
  private readonly debounceTime = 200;
  private readonly selectDebounceTime = 100;

  private readonly standardFormPipe =  pipe(
        // debounceTime<string>(this.debounceTime), //may or may not want
        distinctUntilChanged<string>(),
        takeUntil(this.ngUnsubscribe)
  );

  theme = myCustomTheme;
  private gridApi: any;
  private gridColumnApi: any;
  rowData: User[] = [];
  userDetailsInfo$ = this.store.select(selectUserInfo);
  subs: Subscription[] = [];
  roles$ = this.store.select(selectUserRoles);
  orgs$ = this.store.select(selectUserAdminFilteredOrgs);
  roleValues: IDropdown[] = [];
  orgValues: Organization[] = [];

  form$ = this.store.select(selectUserAdminForm);
  isSuperUserNotMatrixPPAAdmin = false;
  isAdminNotSuperUser = false;




  public gridOptions: GridOptions = {
    rowSelection: "single",
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    editType: "fullRow",
    onGridReady: this.onGridReady.bind(this),
  };
  colDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    flex: 1,
    filter: true,
    editable: false,
  };

  userAdminForm: FormGroup = new FormGroup({
      userSearch: new FormControl(''),
  })



  ngOnInit() {
    this.store.dispatch(UserAdminActions.pageLoaded());

    this.userAdminForm.controls['userSearch'].valueChanges.pipe(
      this.standardFormPipe
    ).subscribe((value: string) => {
      // this.store.dispatch(UserAdminActions.userSearchChanged({ value }));

        this.gridApi!.setGridOption("quickFilterText", value);
    });
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  addNewUser() {
    this.dialog.open(EditAddUserModalComponent, {
      disableClose: true,
      data: {
        title: "Create User",
      },
    });
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    this.subs.push(
      this.store.select(selectIsSuperUserNotMatrixPPAAdmin).subscribe((isSuperUser) => {
        this.isSuperUserNotMatrixPPAAdmin = isSuperUser;
        if (this.gridApi) {
          this.gridApi.refreshCells({ force: true });
        }
      })
    );

    this.subs.push(
      this.store.select(selectIsAdminNotSuperUser).subscribe((isAdmin) => {
        this.isAdminNotSuperUser = isAdmin;
        if (this.gridApi) {
          this.gridApi.refreshCells({ force: true });
        }
      })
    );

    this.subs.push(
      this.store.select(selectUsers).subscribe((users) => {
        if (users.length > 0) {
          this.rowData = users;
          this.gridApi.setGridOption("loading", false);
        } else {
          this.gridApi.setGridOption("loading", true);
        }
      })
    );
    
    this.subs.push(
      this.roles$.subscribe((roles) => {
        this.roleValues = roles;
        if (this.orgValues.length > 0) {
          this.setColDefs();
          if (this.gridApi) {
            this.gridApi.refreshCells();
          }
        }
      })
    );

    this.subs.push(
      this.orgs$.subscribe((orgs) => {
        this.orgValues = orgs;
        if (this.roleValues.length > 0) {
          this.setColDefs();
          if (this.gridApi) {
            this.gridApi.refreshCells();
          }
        }
      })
    );
  }

  setColDefs() {
    this.colDefs = [
      {
        field: "id",
        editable: false,
        headerName: "User Id",
        suppressNavigable: true,
      },
      {
        field: "firstName",
        tooltipValueGetter: (p: any) => p.value,
      },
      {
        field: "lastName",
        tooltipValueGetter: (p: any) => p.value,
      },
      { field: "emailAddress", tooltipValueGetter: (p: any) => p.value },
      {
        field: "roleId",
        headerName: "Role",
        tooltipValueGetter: (params: any) =>
          this.formatSelectData(params.value, this.roleValues),
        valueFormatter: (params: any) =>
          this.formatSelectData(params.value, this.roleValues),
        filterValueGetter: (params: any) => {
          if (!this.roleValues || this.roleValues.length === 0) {
            return '';
          }
          const roleId = params.data?.roleId;
          return this.formatSelectData(roleId, this.roleValues);
        },
      },
      {
        field: "organizationIds",
        headerName: "Organizations",
        flex: 1.5,
        tooltipValueGetter: (params: any) =>
          this.formatMultipleOrganizations(params.value),
        valueFormatter: (params: any) =>
          this.formatMultipleOrganizations(params.value),
        filterValueGetter: (params: any) => {
          if (!this.orgValues || this.orgValues.length === 0) {
            return '';
          }
          const organizationIds = params.data?.organizationIds;
          return this.formatMultipleOrganizations(organizationIds);
        },
      },
      {
        field: "lastLogin",
        headerName: "Last Login",
        cellDataType: "date",
        tooltipValueGetter: (p: any) => p.value ? formatDate(p.value, 'short', 'en-us') : '',
        valueFormatter: (p: any) =>
          p.value ? formatDate(p.value, 'short', 'en-us') : '',
      },
      {
        field: "active",
        filter: false,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
        cellDataType: "boolean",
        valueGetter: function (params) {
          return params.data.active === 1;
        },
      },
      {
        field: "actions",
        filter: false,
        editable: false,
        suppressNavigable: true,
        cellRenderer: EditDeleteRendererComponent,
        cellRendererParams: (params: any) => ({
          showDelete: false,
          showEdit: true,
          disableEdit: (this.isSuperUserNotMatrixPPAAdmin && params.data?.roleId === RoleConstants.MATRIX_PPA_ADMIN)
            || (this.isAdminNotSuperUser && ROLE_GROUPS.SUPER_USER.includes(params.data?.roleId))
        })
      },
    ];

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.colDefs);
    }
  }

  formatSelectData(id: string, options: any[]): string {
    if (!options || options.length === 0) {
      return '';
    }
    
    const value = options.find((option: { id: any }) => {
      return option.id == id || option.id === id;
    });
    
    return value?.name || '';
  }

  formatMultipleOrganizations(organizationIds: number[] | number | null | undefined): string {
    if (!organizationIds) {
      return '';
    }
    if (Array.isArray(organizationIds) && organizationIds.length === 0) {
      return '';
    }
    if (typeof organizationIds === 'number') {
      return this.formatSelectData(organizationIds.toString(), this.orgValues);
    }

    if (Array.isArray(organizationIds)) {
      const orgNames = organizationIds
        .map(id => this.formatSelectData(id.toString(), this.orgValues))
        .filter(name => name !== '')
        .join(', ');
      
      return orgNames || '';
    }
    return '';
  }

  onCellClicked(params: any) {
    if (
      params.column.colId === "actions" &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;
      if (action === "edit") {
        this.dialog.open(EditAddUserModalComponent, {
          disableClose: true,
          data: {
            user: params.data,
            title: "Update User",
          },
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => {
      sub?.unsubscribe();
    });
  }
}
